/**
 * One implementation of "make the interface speak this language".
 *
 * The same twenty lines used to live in three places - the website mixin, the
 * home page component and the store's initializeLayout - each with its own
 * try/catch around vue-i18n's two APIs. That is how `/about` ended up able to
 * disagree with `/` about the current text direction.
 */

import i18n from '@/i18n';
import store from '@/state/store';
import { DEFAULT_LOCALE, isRtl } from './publicPages';

/**
 * vue-i18n exposes `locale` as a plain string in legacy mode and as a ref in
 * composition mode. The app is configured for the latter, but the distinction
 * has bitten this codebase before, so read it defensively in one place instead
 * of at every call site.
 */
function localeRef() {
  const global = (i18n && i18n.global) || i18n;
  return global || null;
}

/** The locale the interface is currently rendering in. */
export function currentLocale() {
  const global = localeRef();
  if (!global) return DEFAULT_LOCALE;
  const value = global.locale;
  const resolved = value && typeof value === 'object' && 'value' in value ? value.value : value;
  return resolved || DEFAULT_LOCALE;
}

/**
 * Apply `locale` everywhere it is observable: vue-i18n, the <html> element, and
 * the persisted preference that the signed-in app reads on boot.
 *
 * Persisting is deliberate. A visitor who arrives on /en/ is reading English,
 * and the registration screen they are one click away from should not switch
 * back to Hebrew under them.
 */
export function applyLocale(locale) {
  if (!locale) return currentLocale();

  const global = localeRef();
  if (global) {
    try {
      if (global.locale && typeof global.locale === 'object' && 'value' in global.locale) {
        global.locale.value = locale;
      } else {
        global.locale = locale;
      }
    } catch (error) {
      console.error('Could not set the i18n locale:', error);
    }
  }

  const direction = isRtl(locale) ? 'rtl' : 'ltr';
  const html = document.documentElement;
  html.setAttribute('lang', locale);
  html.setAttribute('dir', direction);

  const layout = store.state.layout;
  if (layout && layout.language !== locale) {
    store.dispatch('layout/setLanguage', { language: locale });
    store.dispatch('layout/setDirection', { direction });
  }

  return locale;
}
